import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';
import type { LibraryRecording } from '../../interfaces/recording';
import { recordingsService, type FetchComposerRecordingsOptions } from '../../services/recordings';

type ComposerRecordingsStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type ComposerRecordingsEntry = {
  recordingIds: string[];
  status: ComposerRecordingsStatus;
  error: string | null;
};

type ComposerRecordingsState = {
  byComposerId: Record<string, ComposerRecordingsEntry>;
  byRecordingId: Record<string, LibraryRecording>;
};

const ensureEntry = (
  state: ComposerRecordingsState,
  composerId: string
): ComposerRecordingsEntry => {
  const existing = state.byComposerId[composerId];
  if (existing) {
    return existing;
  }

  const entry: ComposerRecordingsEntry = {
    recordingIds: [],
    status: 'idle',
    error: null,
  };
  state.byComposerId[composerId] = entry;
  return entry;
};

type LoadComposerRecordingsArgs = {
  composerId: string;
  force?: boolean;
  composerName?: string;
  limit?: number;
  offset?: number;
};

const loadComposerRecordingsInternal = createAsyncThunk<
  { composerId: string; recordings: LibraryRecording[] },
  LoadComposerRecordingsArgs,
  { state: RootState; rejectValue: { composerId: string; error: string } }
>(
  'composerRecordings/load',
  async ({ composerId, composerName, limit, offset }, { rejectWithValue }) => {
    try {
      const options: FetchComposerRecordingsOptions = {};
      if (typeof limit === 'number') {
        options.limit = limit;
      }
      if (typeof offset === 'number') {
        options.offset = offset;
      }
      if (composerName) {
        options.composerName = composerName;
      }

      const recordings = await recordingsService.fetchComposerRecordings(composerId, options);
      return { composerId, recordings };
    } catch (error: any) {
      const message =
        error?.response?.data?.error ??
        error?.message ??
        'Failed to load recordings';
      return rejectWithValue({ composerId, error: String(message) });
    }
  },
  {
    condition: ({ composerId, force }, { getState }) => {
      if (!composerId) {
        return false;
      }
      if (force) {
        return true;
      }

      const state = getState();
      const entry = state.composerRecordings?.byComposerId?.[composerId];

      if (!entry) {
        return true;
      }
      if (entry.status === 'loading') {
        return false;
      }
      if (entry.status === 'succeeded') {
        return false;
      }
      return true;
    },
  }
);

const initialState: ComposerRecordingsState = {
  byComposerId: {},
  byRecordingId: {},
};

const composerRecordingsSlice = createSlice({
  name: 'composerRecordings',
  initialState,
  reducers: {
    clearComposerRecordings(state, action: PayloadAction<{ composerId: string }>) {
      const { composerId } = action.payload;
      delete state.byComposerId[composerId];
    },
    resetComposerRecordings(state) {
      state.byComposerId = {};
      state.byRecordingId = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadComposerRecordingsInternal.pending, (state, action) => {
      const { composerId } = action.meta.arg;
      const entry = ensureEntry(state, composerId);
      entry.status = 'loading';
      entry.error = null;
    });

    builder.addCase(loadComposerRecordingsInternal.fulfilled, (state, action) => {
      const { composerId, recordings } = action.payload;
      const entry = ensureEntry(state, composerId);

      const ids: string[] = [];
      for (const recording of recordings) {
        if (!recording || !recording.id) {
          continue;
        }
        state.byRecordingId[recording.id] = recording;
        ids.push(recording.id);
      }

      entry.recordingIds = Array.from(new Set(ids));
      entry.status = 'succeeded';
      entry.error = null;
    });

    builder.addCase(loadComposerRecordingsInternal.rejected, (state, action) => {
      if (action.meta.aborted) {
        return;
      }
      const { composerId } = action.meta.arg;
      const entry = ensureEntry(state, composerId);
      entry.status = 'failed';

      if (action.payload?.error) {
        entry.error = action.payload.error;
      } else {
        entry.error = action.error.message ?? 'Failed to load recordings';
      }
    });
  },
});

const loadComposerRecordingsWithArgs = (
  composerId: string,
  options?: { force?: boolean; composerName?: string; limit?: number; offset?: number }
) =>
  loadComposerRecordingsInternal({
    composerId,
    force: options?.force,
    composerName: options?.composerName,
    limit: options?.limit,
    offset: options?.offset,
  });

export const loadComposerRecordings = Object.assign(loadComposerRecordingsWithArgs, {
  pending: loadComposerRecordingsInternal.pending,
  fulfilled: loadComposerRecordingsInternal.fulfilled,
  rejected: loadComposerRecordingsInternal.rejected,
  typePrefix: loadComposerRecordingsInternal.typePrefix,
});

export const selectComposerRecordings =
  (composerId: string) =>
  (state: RootState): LibraryRecording[] => {
    const entry = state.composerRecordings.byComposerId[composerId];
    if (!entry) {
      return [];
    }

    const recordings: LibraryRecording[] = [];
    for (const id of entry.recordingIds) {
      const recording = state.composerRecordings.byRecordingId[id];
      if (recording) {
        recordings.push(recording);
      }
    }
    return recordings;
  };

export const selectComposerRecordingsStatus =
  (composerId: string) =>
  (state: RootState): ComposerRecordingsStatus =>
    state.composerRecordings.byComposerId[composerId]?.status ?? 'idle';

export const selectComposerRecordingsError =
  (composerId: string) =>
  (state: RootState): string | null =>
    state.composerRecordings.byComposerId[composerId]?.error ?? null;

export const composerRecordingsActions = {
  ...composerRecordingsSlice.actions,
  loadComposerRecordings,
};

export default composerRecordingsSlice.reducer;

export type { ComposerRecordingsStatus };
